import { useTranslation } from "react-i18next";
import type { DataCollectionResponse } from "@/api";
import { Text } from "@/components/Text";

/** The sync schedule in words; shared by the Content tab and Build with AI's data source card. */

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export interface ScheduleValue {
  type: "daily" | "weekly";
  runAtTime: string;
  daysOfWeek: number[];
}

/** Read the stored sync_schedule from the collection's config. */
export function readSchedule(source: DataCollectionResponse): ScheduleValue | null {
  const raw = source.config?.sync_schedule as Record<string, unknown> | undefined;
  if (!raw || typeof raw !== "object") return null;
  return {
    type: raw.type === "weekly" ? "weekly" : "daily",
    runAtTime: typeof raw.run_at_time === "string" ? raw.run_at_time : "09:00",
    daysOfWeek: Array.isArray(raw.days_of_week) ? (raw.days_of_week as number[]) : [],
  };
}

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function ScheduleSummary({ schedule }: Readonly<{ schedule: ScheduleValue | null }>) {
  const { t } = useTranslation("dataSources");
  if (!schedule) {
    return (
      <Text variant="body-sm" tone="tertiary">
        {t("sync.none")}
      </Text>
    );
  }
  return (
    <Text variant="body-sm">
      {schedule.type === "weekly"
        ? t("sync.weeklyAt", {
            days: schedule.daysOfWeek.map((d) => DAYS[d]).join(", "),
            time: schedule.runAtTime,
          })
        : t("sync.dailyAt", { time: schedule.runAtTime })}
    </Text>
  );
}
